/* eslint-disable no-use-before-define */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { all, call, put, takeEvery } from 'redux-saga/effects'

import Api from '@/api/_legacy'
import {
  ApiAccountApprovalEvent,
  ApiCPPIStrategy,
  ApiDeposit,
  ApiFee,
  ApiLoan,
  ApiNote,
  ApiTIPPStrategy,
  ApiTradeEconomics
} from '@/api/_legacy/generated-api'
import { UserRole, UserType } from '@/types/user'
import { createLogoutExtraReducers } from '@/utilities/redux'

import { TENANT_ID } from '../../../../tradebuilder-v2/constants'
import { SavedState, TradeActivationStep } from '../../../../tradebuilder-v2/types'
import { Errors } from '../../../../tradebuilder-v2/util'
import { parseApproveErrors } from '../../../util/validator'
import { validate } from './validation_rules'

export type SetEconomicsFormState = {
  counterPartyId: string
  depositId: string
  hasBankGuarantee?: boolean
  loanId: string
  noteFees: ApiFee[]
  portfolioFees: ApiFee[]
  strategyFees: ApiFee[]
  strategyTemplateId: string
}

export type SetEconomicsStepState = {
  accountId: string
  approvalEvents: ApiAccountApprovalEvent[]
  availableFeeTypes: string[]
  canProceed: boolean
  counterpartyIds: string[]
  depositInstruments: ApiDeposit[]
  errors: Errors
  formState: SetEconomicsFormState
  generalErrors?: []
  hasCompleted: boolean
  isExistingNote: boolean
  loanInstruments: ApiLoan[]
  note: ApiNote
  status: TradeActivationStep
  success?: boolean
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

export type SetEconomicsInitialise = {
  approvalEvents: ApiAccountApprovalEvent[]
  savedState: SavedState
  status: TradeActivationStep
  userName: string
  userRoles: UserRole[]
  userType: UserType
}

const initialState: SetEconomicsStepState = {
  accountId: undefined,
  isExistingNote: false,
  counterpartyIds: [],
  depositInstruments: [],
  loanInstruments: [],
  note: undefined,
  userName: undefined,
  userType: undefined,
  userRoles: [],
  hasCompleted: false,
  formState: {
    counterPartyId: '',
    depositId: '',
    loanId: '',
    noteFees: [],
    portfolioFees: [],
    strategyFees: [],
    strategyTemplateId: ''
  },
  canProceed: true,
  errors: {},
  generalErrors: [],
  approvalEvents: [],
  availableFeeTypes: [],
  status: TradeActivationStep.unDef
}

export const setEconomicsSlice = createSlice({
  name: 'setEconomicsStep',
  initialState,
  reducers: {
    reset: state => {
      state.formState = initialState.formState
      state.canProceed = true
      state.hasCompleted = false
      state.errors = {}
      state.success = undefined
    },
    setInvalid: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
      state.canProceed = false
    },
    setValid: state => {
      state.errors = {}
      state.canProceed = true
    },
    initialise: (state, action: PayloadAction<SetEconomicsInitialise>) => {
      state.userName = action.payload.userName
      state.userType = action.payload.userType
      state.userRoles = action.payload.userRoles
      state.approvalEvents = action.payload.approvalEvents
        ? action.payload.approvalEvents.filter(x => x.statusBefore === 'PendingEconomicsApproval')
        : []
      state.status = action.payload.status
      state.hasCompleted = action.payload.status > TradeActivationStep.setEconomics
      state.success = undefined
      const { savedState } = action.payload
      if (savedState) {
        state.isExistingNote = savedState.note.associatedTrades?.length > 0
        const { pendingAccountState } = savedState.account
        state.accountId = action.payload.savedState.account.accountId
        state.formState.noteFees = []
        state.formState.portfolioFees = []
        state.formState.strategyFees = []

        if (pendingAccountState && pendingAccountState.noteOverrideFees) {
          state.formState.noteFees = pendingAccountState.noteOverrideFees
        } else if (savedState.note && savedState.note.fees) {
          state.formState.noteFees = savedState.note.fees
        } else {
          state.formState.noteFees = savedState.client.defaultFees.defaultNoteFees
        }

        if (pendingAccountState && pendingAccountState.portfolioOverrideFees) {
          state.formState.portfolioFees = pendingAccountState.portfolioOverrideFees
        } else if (
          pendingAccountState &&
          pendingAccountState.portfolio &&
          pendingAccountState.portfolio.fees
        ) {
          state.formState.portfolioFees = pendingAccountState.portfolio.fees
        } else if (savedState.currentPortfolio && savedState.currentPortfolio.fees) {
          state.formState.portfolioFees = savedState.currentPortfolio.fees
        } else {
          state.formState.portfolioFees = savedState.client.defaultFees.defaultPortfolioFees
        }

        if (pendingAccountState && pendingAccountState.strategyOverrideFees) {
          state.formState.strategyFees = pendingAccountState.strategyOverrideFees
        } else if (
          pendingAccountState &&
          pendingAccountState.strategy &&
          pendingAccountState.strategy.fees
        ) {
          state.formState.strategyFees = pendingAccountState.strategy.fees
        } else if (
          pendingAccountState &&
          pendingAccountState.strategy &&
          pendingAccountState.strategyTemplateId
        ) {
          if (savedState.client.defaultFees.defaultStrategyFees) {
            const defaultStrategyFees = savedState.client.defaultFees.defaultStrategyFees.find(
              dsf => dsf.strategyTemplateId === pendingAccountState.strategyTemplateId
            )
            if (defaultStrategyFees) {
              state.formState.strategyFees = defaultStrategyFees.fees
            }
          }
        } else if (savedState.currentStrategy && savedState.currentStrategy.fees) {
          state.formState.strategyFees = savedState.currentStrategy.fees
        }

        state.formState.counterPartyId = savedState.note?.counterPartyId
          ? savedState.note.counterPartyId
          : savedState.client.tradeBuildingSettings.defaultNoteCounterpartyId
        state.formState.loanId = pendingAccountState.loanId
          ? pendingAccountState.loanId
          : savedState.client.defaultCashInstruments.loanId
        state.formState.depositId = pendingAccountState.depositId
          ? pendingAccountState.depositId
          : savedState.client.defaultCashInstruments.depositId
        state.formState.strategyTemplateId = pendingAccountState.strategyTemplateId
          ? pendingAccountState.strategyTemplateId
          : savedState.account.currentAccountState.strategyTemplateId
        if (pendingAccountState.strategy) {
          if (pendingAccountState.strategy.type === 'CPPI') {
            const strategy = pendingAccountState.strategy as ApiCPPIStrategy
            state.formState.hasBankGuarantee = strategy.hasBankGuarantee
          }
          if (pendingAccountState.strategy.type === 'TIPP') {
            const strategy = pendingAccountState.strategy as ApiTIPPStrategy
            state.formState.hasBankGuarantee = strategy.hasBankGuarantee
          }
        } else {
          if (savedState.account.currentAccountState.strategy.type === 'CPPI') {
            const strategy = savedState.account.currentAccountState.strategy as ApiCPPIStrategy
            state.formState.hasBankGuarantee = strategy.hasBankGuarantee
          }
          if (savedState.account.currentAccountState.strategy.type === 'TIPP') {
            const strategy = savedState.account.currentAccountState.strategy as ApiTIPPStrategy
            state.formState.hasBankGuarantee = strategy.hasBankGuarantee
          }
        }
      }
    },
    fetchCounterpartyIdsSuccess: (state, action: PayloadAction<string[]>) => {
      state.counterpartyIds = action.payload
    },
    fetchFeeTypesSuccess: (state, action: PayloadAction<string[]>) => {
      state.availableFeeTypes = action.payload
    },
    fetchCounterpartyIdsFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    fetchDepositInstrumentsSuccess: (state, action: PayloadAction<ApiDeposit[]>) => {
      state.depositInstruments = action.payload
    },
    fetchLoanInstrumentsSuccess: (state, action: PayloadAction<ApiLoan[]>) => {
      state.loanInstruments = action.payload
    },
    fetchDepositInstrumentsFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
    },
    postTradeApproveSuccess: state => {
      state.errors = {}
      state.success = true
    },
    postTradeApproveFailure: (state, action: PayloadAction<Errors>) => {
      state.errors = action.payload
      state.success = false
    },
    requestMoveNext: () => {
      // dummy comment to keep linter happy
    },
    moveNext: () => {
      // dummy comment to keep linter happy
    },
    moveBack: () => {
      // dummy comment to keep linter happy
    }
  },
  extraReducers: builder => {
    const logoutReducers = createLogoutExtraReducers(initialState)
    Object.entries(logoutReducers).forEach(([actionType, reducer]) => {
      builder.addCase(actionType, reducer)
    })
  }
})

export const postTradeApprove =
  (accountId: string, formState: SetEconomicsFormState) => async dispatch => {
    try {
      const request: ApiTradeEconomics = {
        depositIdOverride: formState.depositId,
        loanIdOverride: formState.loanId,
        noteCounterpartyId: formState.counterPartyId,
        noteOverrideFees: formState.noteFees,
        portfolioOverrideFees: formState.portfolioFees,
        strategyOverrideFees: formState.strategyFees
      }

      const validationResult = validate(formState)

      if (!validationResult.isValid) {
        dispatch(actions.setInvalid(validationResult.errors))
        return
      }

      await Api.tenant.approveEconomicsCreate(accountId, TENANT_ID, request, { format: 'json' })
      dispatch(actions.postTradeApproveSuccess())
    } catch (response) {
      const errors: Errors = parseApproveErrors(response)
      dispatch(actions.postTradeApproveFailure(errors))
    }
  }

function* fetchCounterpartyIds() {
  try {
    const response = yield call(Api.tenant.counterpartyidsDetail, TENANT_ID)
    yield put({
      type: 'setEconomicsStep/fetchCounterpartyIdsSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'setEconomicsStep/fetchCounterpartyIdsFailure', payload: error })
  }
}
function* fetchAvailableFees() {
  try {
    const response = yield call(Api.tenant.feetypeDetail, TENANT_ID)
    yield put({
      type: 'setEconomicsStep/fetchFeeTypesSuccess',
      payload: response.data
    })
  } catch (error) {
    yield put({ type: 'setEconomicsStep/fetchFeeTypesFailure', payload: error })
  }
}
function* fetchInstruments() {
  try {
    const response = yield call(Api.tenant.cashinstrumentDetail, TENANT_ID)
    const loanInstruments = response.data.filter(item => item.type === 'Loan')
    const depositInstruments = response.data.filter(item => item.type === 'Deposit')
    yield put({
      type: 'setEconomicsStep/fetchDepositInstrumentsSuccess',
      payload: depositInstruments
    })
    yield put({
      type: 'setEconomicsStep/fetchLoanInstrumentsSuccess',
      payload: loanInstruments
    })
  } catch (error) {
    yield put({ type: 'setEconomicsStep/fetchDepositInstrumentsFailure', payload: error })
  }
}

function* fetchReferenceDataSaga() {
  yield all([fetchCounterpartyIds(), fetchInstruments(), fetchAvailableFees()])
}

export function* setEconomicsSaga() {
  yield all([takeEvery('setEconomicsStep/initialise', fetchReferenceDataSaga)])
}

export const actions = { ...setEconomicsSlice.actions }

export type TradeSetEconomicsActions = typeof actions

export default setEconomicsSlice.reducer
